import styled from '@emotion/styled';
import {
  faCheckCircle,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import {
  faCircleThreeQuartersStroke,
  faShieldCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useUser } from '~/common/hooks/useAuthentication';

import { CATEGORY_IDS } from './constants';

const StyledFontAwesomeIcon = styled<any>(FontAwesomeIcon)`
  margin-top: 1px;
  margin-right: 10px;
  color: ${(props) => {
    switch (props.color) {
      case 'red':
        return props.theme.colors?.red[600];
      case 'yellow':
        return props.theme.colors?.orange[300];
      case 'green':
      default:
        return props.theme.colors?.green[600];
    }
  }};
  font-size: ${(props) => props.theme.functions?.pixelsToRem(20)};
`;

const StyledNotification = styled.span<any>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  height: 20px;
  width: 20px;
  min-height: 20px;
  min-width: 20px;
  border-radius: 50%;
  line-height: 150%;
  margin-top: 1px;
  margin-right: 10px;
  color: ${(props) => props.theme.colors?.black};
  font-size: ${(props) => props.theme.functions?.pixelsToRem(12)};
  border: 1px solid ${(props) => props.theme.colors?.orange[300]};
  background-color: ${(props) => props.theme.colors?.orange[300]};
`;

type NotificationProps = {
  currentCategoryId: number;
  numberOfActiveAlerts?: number;
  isVerified?: boolean;
};

const RenderIcon = (
  iconProps: Readonly<React.ComponentProps<typeof StyledFontAwesomeIcon>>,
) => <StyledFontAwesomeIcon {...iconProps} />;

const Notification = ({
  currentCategoryId,
  numberOfActiveAlerts,
  isVerified,
}: NotificationProps) => {
  const { isUnderwriter, hasAccessToUnderwriterView } = useUser();
  const isUnderwriterOrTracUser = isUnderwriter || hasAccessToUnderwriterView;

  const isClassify = currentCategoryId === CATEGORY_IDS.CLASSIFY;

  if (numberOfActiveAlerts > 0) {
    if (isClassify) {
      return RenderIcon({
        color: 'red',
        icon: faExclamationCircle,
        'data-tracking': 'Classification pending',
        'data-testid': 'classifiedPendingIcon',
        id: 'classifiedPendingIcon',
        alt: 'Classification pending',
      });
    }

    return (
      <StyledNotification
        data-tracking={`${numberOfActiveAlerts} ${
          numberOfActiveAlerts > 1 ? 'pending conditions' : 'pending condition'
        }`}
      >
        {numberOfActiveAlerts}
      </StyledNotification>
    );
  }

  if (isVerified) {
    return RenderIcon({
      color: 'green',
      'data-tracking': 'Underwriter verified',
      icon: faShieldCheck,
      'data-testid': 'underwriterVerifiedIcon',
      id: 'underwriterVerifiedIcon',
      alt: 'Underwriter verified',
    });
  } else if (isUnderwriterOrTracUser) {
    return RenderIcon({
      color: 'green',
      'data-tracking': 'Underwriter complete',
      icon: faCircleThreeQuartersStroke,
      'data-testid': 'underwriterCompleteIcon',
      id: 'underwriterCompleteIcon',
      alt: 'Underwriter complete',
    });
  }
  return RenderIcon({
    color: 'green',
    'data-tracking': 'Broker verified',
    icon: faCheckCircle,
    'data-testid': 'brokerVerifiedIcon',
    id: 'brokerVerifiedIcon',
    alt: 'Broker verified',
  });
};

export default Notification;
